import { ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { LoaderContext, LoaderContextValue } from '../context/LoaderContext';
import { Spinner } from '../components/Spinner/Spinner';
import { ProgressLoader } from 'src/components/ProgressLoader/ProgressLoader';

export function useLoader(): LoaderContextValue {
  return useContext(LoaderContext)!;
}

export function LoaderProvider({ children }: Readonly<{ children: ReactNode }>) {
  const [isVisible, setIsVisible] = useState(false);
  const [progress, setProgress] = useState(0);
  const [isProgress, setIsProgress] = useState(false);

  // The actions only call state setters, so they are created once and stay stable.
  const actions = useMemo(
    () => ({
      updateProgress: (value: number) => {
        setProgress(value);
      },
      show: (options?: { progress?: boolean }) => {
        setIsVisible(true);

        if (options?.progress) {
          setIsProgress(options.progress);
        }
      },
      hide: () => {
        setIsVisible(false);
        setIsProgress(false);
        setProgress(0);
      },
    }),
    [],
  );

  const value = useMemo<LoaderContextValue>(
    () => ({ isVisible, isProgress, progress, ...actions }),
    [isVisible, isProgress, progress, actions],
  );

  return <LoaderContext.Provider value={value}>{children}</LoaderContext.Provider>;
}

export const Loader = () => {
  const { isVisible, isProgress, progress } = useLoader();

  useEffect(() => {
    if (!isVisible) {
      document.body.style.removeProperty('pointer-events');
    }

    return () => {
      if (!isVisible) {
        document.body.style.removeProperty('pointer-events');
      }
    };
  }, [isVisible]);

  if (!isVisible) {
    return null;
  }

  return isProgress ? (
    <ProgressLoader value={progress} fullScreen={true} />
  ) : (
    <Spinner fullScreen={true} />
  );
};
